import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Plus, AlertTriangle, X, Save, Calendar, Check, XCircle, MapPin, Trash2 } from 'lucide-react';
import { api } from '../../api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

type Reservation = {
  id: string;
  guestName: string;
  guestPhone: string | null;
  guestEmail: string | null;
  partySize: number;
  status: string;
  source: string;
  requestedStartAt: string;
  requestedEndAt: string;
  confirmedStartAt: string | null;
  specialRequests: string | null;
};

type Resource = {
  id: string;
  resourceType: 'table' | 'zone' | 'event_slot';
  tableId: string | null;
  zoneId: string | null;
  name: string;
  capacity: number | null;
  isActive: boolean;
};

type ZoneRow = { id: string; name: string };
type TableRow = { id: string; name: string; tableNumber: string; zoneId: string };

type Location = { id: string; name: string };
type Tab = 'reservations' | 'resources';

export function LocationReservationsPage() {
  const { id: locationId } = useParams<{ id: string }>();
  const [location, setLocation] = useState<Location | null>(null);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [tables, setTables] = useState<TableRow[]>([]);
  const [tab, setTab] = useState<Tab>('reservations');
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [creatingResource, setCreatingResource] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<Reservation | null>(null);
  const [deleteResourceTarget, setDeleteResourceTarget] = useState<Resource | null>(null);

  const refresh = useCallback(async () => {
    if (!locationId) return;
    try {
      const [loc, res, rsrc, zs, ts] = await Promise.all([
        api.get<Location>(`/admin/locations/${locationId}`),
        api.get<Reservation[]>(`/admin/locations/${locationId}/reservations`),
        api.get<Resource[]>(`/admin/locations/${locationId}/reservation-resources`),
        api.get<ZoneRow[]>(`/admin/locations/${locationId}/zones`),
        api.get<TableRow[]>(`/admin/locations/${locationId}/tables`),
      ]);
      setLocation(loc);
      setReservations(res);
      setResources(rsrc);
      setZones(zs);
      setTables(ts);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    }
  }, [locationId]);

  useEffect(() => { refresh(); }, [refresh]);

  const confirmDeleteResource = async () => {
    if (!deleteResourceTarget) return;
    await api.del(`/admin/locations/${locationId}/reservation-resources/${deleteResourceTarget.id}`);
    refresh();
  };

  const formatDateTime = (iso: string) => new Date(iso).toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

  const statusColors: Record<string, { bg: string; text: string }> = {
    pending: { bg: 'rgba(245,158,11,0.15)', text: '#fcd34d' },
    confirmed: { bg: 'rgba(16,185,129,0.15)', text: '#6ee7b7' },
    rejected: { bg: 'rgba(239,68,68,0.15)', text: '#fca5a5' },
    seated: { bg: 'rgba(59,130,246,0.15)', text: '#93c5fd' },
    completed: { bg: 'rgba(100,116,139,0.2)', text: '#94a3b8' },
    cancelled: { bg: 'rgba(100,116,139,0.2)', text: '#94a3b8' },
    no_show: { bg: 'rgba(239,68,68,0.15)', text: '#fca5a5' },
  };

  const handleConfirm = async (id: string) => {
    await api.post(`/admin/locations/${locationId}/reservations/${id}/confirm`, {});
    refresh();
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    await api.post(`/admin/locations/${locationId}/reservations/${rejectTarget.id}/reject`, {});
    refresh();
  };

  return (
    <div>
      <Link to="/locations" style={backLink}><ChevronLeft size={14} /> Lokacije</Link>
      <header style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            <Calendar size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Rezervacije: {location?.name || '...'}
          </h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Bookings lifecycle + resursi za rezervaciju ({reservations.length} rezervacija · {resources.length} resursa)
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {tab === 'reservations' ? (
          <button onClick={() => setCreating(true)} style={btnPrimary}>
            <Plus size={14} /> Nova rezervacija
          </button>
        ) : (
          <button onClick={() => setCreatingResource(true)} style={btnPrimary}>
            <Plus size={14} /> Novi resurs
          </button>
        )}
      </header>

      <div style={tabsBar}>
        <button onClick={() => setTab('reservations')} style={{ ...tabBtn, ...(tab === 'reservations' ? tabBtnActive : {}) }}>
          <Calendar size={13} /> Rezervacije ({reservations.length})
        </button>
        <button onClick={() => setTab('resources')} style={{ ...tabBtn, ...(tab === 'resources' ? tabBtnActive : {}) }}>
          <MapPin size={13} /> Resursi ({resources.length})
        </button>
      </div>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

      {tab === 'resources' && (
        <div style={card}>
          {resources.length === 0 ? (
            <div style={emptyState}>
              <MapPin size={32} style={{ opacity: 0.3, margin: '0 auto 8px' }} />
              <div>Nema resursa za rezervaciju</div>
              <div style={{ fontSize: 12, marginTop: 6 }}>
                Resurs = sto, zona ili event slot koji gost može rezervisati. Dodaj bar jedan pre pravljenja rezervacije.
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: 'var(--color-bg)' }}>
                  <th style={th}>Naziv</th>
                  <th style={th}>Tip</th>
                  <th style={th}>Veza</th>
                  <th style={{ ...th, textAlign: 'right' }}>Kapacitet</th>
                  <th style={th}>Status</th>
                  <th style={{ ...th, width: 80, textAlign: 'right' }}>Akcije</th>
                </tr></thead>
                <tbody>
                  {resources.map((r) => {
                    const linkName = r.tableId
                      ? (tables.find((t) => t.id === r.tableId)?.name || '-')
                      : r.zoneId
                        ? (zones.find((z) => z.id === r.zoneId)?.name || '-')
                        : '-';
                    const typeBadge = r.resourceType === 'table' ? { bg: 'rgba(59,130,246,0.15)', color: '#93c5fd', label: 'Sto' }
                      : r.resourceType === 'zone' ? { bg: 'rgba(245,158,11,0.15)', color: '#fcd34d', label: 'Zona' }
                      : { bg: 'rgba(168,85,247,0.15)', color: '#c4b5fd', label: 'Event slot' };
                    return (
                      <tr key={r.id}>
                        <td style={{ ...td, fontWeight: 600 }}>{r.name}</td>
                        <td style={td}>
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, background: typeBadge.bg, color: typeBadge.color, fontWeight: 700 }}>
                            {typeBadge.label}
                          </span>
                        </td>
                        <td style={{ ...td, fontSize: 12, color: 'var(--color-text-muted)' }}>{linkName}</td>
                        <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.capacity ?? '-'}</td>
                        <td style={td}>
                          {r.isActive
                            ? <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(16,185,129,0.15)', color: '#6ee7b7', fontWeight: 700 }}>Aktivan</span>
                            : <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(100,116,139,0.2)', color: '#94a3b8', fontWeight: 700 }}>Neaktivan</span>
                          }
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <button onClick={() => setDeleteResourceTarget(r)} style={{ ...iconBtn, color: '#fca5a5' }} title="Obriši">
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'reservations' && <div style={card}>
        {reservations.length === 0 ? (
          <div style={emptyState}>
            <Calendar size={32} style={{ opacity: 0.3, margin: '0 auto 8px' }} />
            <div>Nema rezervacija</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Gost</th><th style={th}>Telefon</th>
                <th style={{ ...th, textAlign: 'right' }}>Osoba</th>
                <th style={th}>Termin</th><th style={th}>Source</th><th style={th}>Status</th>
                <th style={{ ...th, width: 120, textAlign: 'right' }}>Akcije</th>
              </tr></thead>
              <tbody>
                {reservations.map((r) => {
                  const c = statusColors[r.status] || statusColors.pending;
                  return (
                    <tr key={r.id}>
                      <td style={{ ...td, fontWeight: 600 }}>{r.guestName}</td>
                      <td style={{ ...td, fontFamily: 'monospace', fontSize: 12, color: 'var(--color-text-muted)' }}>{r.guestPhone || '-'}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{r.partySize}</td>
                      <td style={{ ...td, fontSize: 12, color: 'var(--color-text-muted)' }}>{formatDateTime(r.requestedStartAt)}</td>
                      <td style={{ ...td, fontSize: 11, color: 'var(--color-text-muted)' }}>{r.source}</td>
                      <td style={td}><span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, background: c.bg, color: c.text, fontWeight: 700 }}>{r.status}</span></td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        {r.status === 'pending' && (
                          <>
                            <button onClick={() => handleConfirm(r.id)} style={{ ...iconBtn, color: '#6ee7b7' }} title="Potvrdi"><Check size={13} /></button>
                            <button onClick={() => setRejectTarget(r)} style={{ ...iconBtn, color: '#fca5a5' }} title="Odbij"><XCircle size={13} /></button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>}

      {creating && <ReservationFormDialog locationId={locationId!} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); refresh(); }} />}
      {creatingResource && (
        <ResourceFormDialog
          locationId={locationId!}
          zones={zones}
          tables={tables}
          onClose={() => setCreatingResource(false)}
          onSaved={() => { setCreatingResource(false); refresh(); }}
        />
      )}

      {rejectTarget && (
        <ConfirmDialog
          title="Odbij rezervaciju"
          message={`Da li želite da odbijete rezervaciju za "${rejectTarget.guestName}"?`}
          confirmLabel="Odbij"
          variant="danger"
          onConfirm={handleReject}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {deleteResourceTarget && (
        <ConfirmDialog
          title="Obriši resurs"
          message={`Da li želite da obrišete resurs "${deleteResourceTarget.name}"? Ako ima istoriju rezervacija, brisanje će biti blokirano.`}
          confirmLabel="Obriši"
          variant="danger"
          onConfirm={confirmDeleteResource}
          onClose={() => setDeleteResourceTarget(null)}
        />
      )}
    </div>
  );
}

function ReservationFormDialog({ locationId, onClose, onSaved }: { locationId: string; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [partySize, setPartySize] = useState(2);
  const now = new Date();
  now.setHours(now.getHours() + 1);
  const [start, setStart] = useState(now.toISOString().slice(0, 16));
  const end = new Date(now.getTime() + 90 * 60 * 1000);
  const [endTime, setEndTime] = useState(end.toISOString().slice(0, 16));
  const [specialRequests, setSpecialRequests] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim()) { setError('Ime gosta obavezno'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/admin/locations/${locationId}/reservations`, {
        guestName: guestName.trim(),
        guestPhone: guestPhone || undefined,
        partySize,
        requestedStartAt: new Date(start).toISOString(),
        requestedEndAt: new Date(endTime).toISOString(),
        specialRequests: specialRequests || undefined,
        source: 'admin',
      });
      onSaved();
    } catch (e: any) { setError(e?.data?.message || e?.message || 'Greška'); }
    finally { setSaving(false); }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>Nova rezervacija</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Ime gosta *">
            <input type="text" value={guestName} onChange={(e) => setGuestName(e.target.value)} autoFocus required style={input} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
            <Field label="Telefon">
              <input type="tel" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} placeholder="+381 60..." style={input} />
            </Field>
            <Field label="Broj osoba *">
              <input type="number" min={1} value={partySize} onChange={(e) => setPartySize(parseInt(e.target.value, 10) || 1)} required style={input} />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Početak *">
              <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} required style={input} />
            </Field>
            <Field label="Kraj *">
              <input type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)} required style={input} />
            </Field>
          </div>
          <Field label="Specijalni zahtevi">
            <textarea value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} rows={2} style={{ ...input, fontFamily: 'inherit', resize: 'vertical' }} />
          </Field>
          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}><Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResourceFormDialog({
  locationId,
  zones,
  tables,
  onClose,
  onSaved,
}: {
  locationId: string;
  zones: ZoneRow[];
  tables: TableRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeToClose(onClose);
  const [resourceType, setResourceType] = useState<'table' | 'zone' | 'event_slot'>('table');
  const [name, setName] = useState('');
  const [tableId, setTableId] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [capacity, setCapacity] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setError('Naziv obavezan'); return; }
    if (resourceType === 'table' && !tableId) { setError('Izaberi sto'); return; }
    if (resourceType === 'zone' && !zoneId) { setError('Izaberi zonu'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/admin/locations/${locationId}/reservation-resources`, {
        resourceType,
        tableId: resourceType === 'table' ? tableId : undefined,
        zoneId: resourceType === 'zone' ? zoneId : undefined,
        name: trimmed,
        capacity: capacity === '' ? undefined : capacity,
        isActive: true,
      });
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  // Auto-populate name when table or zone is picked
  const handleTablePick = (id: string) => {
    setTableId(id);
    if (!name.trim()) {
      const t = tables.find((tt) => tt.id === id);
      if (t) setName(t.name);
    }
  };
  const handleZonePick = (id: string) => {
    setZoneId(id);
    if (!name.trim()) {
      const z = zones.find((zz) => zz.id === id);
      if (z) setName(z.name);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>Novi resurs za rezervaciju</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Tip resursa">
            <div style={{ display: 'flex', gap: 8 }}>
              {(['table', 'zone', 'event_slot'] as const).map((t) => (
                <label
                  key={t}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: `1px solid ${resourceType === t ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    borderRadius: 6,
                    fontSize: 12,
                    color: 'var(--color-text)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <input type="radio" value={t} checked={resourceType === t} onChange={() => setResourceType(t)} style={{ marginRight: 6 }} />
                  {t === 'table' ? 'Sto' : t === 'zone' ? 'Zona' : 'Event slot'}
                </label>
              ))}
            </div>
          </Field>

          {resourceType === 'table' && (
            <Field label="Sto *">
              {tables.length === 0 ? (
                <div style={{ padding: 10, fontSize: 12, color: '#fca5a5', background: 'rgba(239,68,68,0.08)', borderRadius: 6 }}>
                  Nema stolova. Prvo ih dodaj kroz Layout stranicu.
                </div>
              ) : (
                <select value={tableId} onChange={(e) => handleTablePick(e.target.value)} required style={input}>
                  <option value="">- izaberi -</option>
                  {tables.map((t) => {
                    const z = zones.find((zz) => zz.id === t.zoneId);
                    return <option key={t.id} value={t.id}>{t.name} {z ? `(${z.name})` : ''}</option>;
                  })}
                </select>
              )}
            </Field>
          )}

          {resourceType === 'zone' && (
            <Field label="Zona *">
              {zones.length === 0 ? (
                <div style={{ padding: 10, fontSize: 12, color: '#fca5a5', background: 'rgba(239,68,68,0.08)', borderRadius: 6 }}>
                  Nema zona. Prvo ih dodaj kroz Layout stranicu.
                </div>
              ) : (
                <select value={zoneId} onChange={(e) => handleZonePick(e.target.value)} required style={input}>
                  <option value="">- izaberi -</option>
                  {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
              )}
            </Field>
          )}

          <Field label="Naziv resursa *">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={resourceType === 'event_slot' ? 'npr. Nova godina 31.12.' : 'Auto-popunjava se iz izbora'}
              required
              style={input}
            />
          </Field>

          <Field label="Kapacitet (broj osoba)">
            <input
              type="number"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value === '' ? '' : parseInt(e.target.value, 10) || '')}
              min={1}
              placeholder="Opcionalno"
              style={input}
            />
          </Field>

          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>{children}</div>;
}

const backLink: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--color-text-muted)', fontSize: 12, marginBottom: 12, textDecoration: 'none' };
const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' };
const emptyState: React.CSSProperties = { padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' };
const th: React.CSSProperties = { padding: '10px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border-light)' };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { padding: '9px 16px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 520, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none' };
const tabsBar: React.CSSProperties = { display: 'flex', gap: 4, marginBottom: 16, padding: 4, background: 'var(--color-bg-surface)', borderRadius: 8, border: '1px solid var(--color-border)', width: 'fit-content' };
const tabBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: 'none', background: 'transparent', color: 'var(--color-text-muted)', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer' };
const tabBtnActive: React.CSSProperties = { background: 'var(--color-bg)', color: 'var(--color-text-strong)' };
